/**
 * useCrossTabDrop — the RECEIVING side of the ⌥Alt+drag cross-tab copy
 * (docs/plans/2026-07-17-cross-tab-drag.md). Window-level native DnD handlers drive the
 * grid store's EXTERNAL-ADD pipeline (the palette drag-to-add machinery):
 *
 * - `dragover`: browsers expose only `dataTransfer.types` before the drop
 *   (protected mode), so the mini-spec parsed FROM the meta type string
 *   feeds `startExternalAdd` and the standard blue landing slot previews
 *   live. Targeting mirrors the in-canvas adapter: `gridUnderPoint` +
 *   `crossGridAccepts` (the meta's contentType stands in for the item) +
 *   the nearest-accepted-child bump.
 * - `drop`: NOW the full payload is readable — validate it (magic/origin/
 *   schema), reconcile against the mini-spec, then RESTART the external add
 *   with the full spec (contentProps included) at the drop point and commit
 *   via `endExternalAdd`: one store commit, one undo gesture, no
 *   empty-props flash. Asset bytes then copy OPFS→OPFS (same origin; the
 *   backend route is the fallback), fire-and-forget — the image renders via
 *   the backend URL regardless.
 * - `dragleave` (off-window) / `dragend`: cancel, clearing the preview. The
 *   SOURCE tab of a successful cross-tab drop does nothing (copy semantics);
 *   an Alt+drag dropped back on its own tab is the duplicate-here freebie —
 *   same code path.
 */
import { useEffect, useRef } from "react";
import { useGridStore } from "@fredericrous/grid";
import type { UpdateDragTarget } from "@fredericrous/grid/store";
import {
  gapOf,
  landingFromRect,
  pxSizeToUnits,
} from "@fredericrous/grid/engine";
import {
  gridUnderPoint,
  nearestAcceptedGridChild,
  type ResolvedGrid,
} from "@fredericrous/grid/headless";

import { crossGridAccepts, inlineRowIn } from "./crossGridAccepts.js";
import {
  WB_ITEM_TYPE,
  parseCrossTabPayload,
  parseMetaType,
  payloadMatchesMeta,
  type CrossTabMeta,
  type CrossTabPayload,
} from "./crossTabDrag.js";
import { getAssetFile, putAsset } from "./assets/opfsAssetStore.js";
import { markDropped } from "./widgets/dropReveal.js";
import { useSiteId } from "./SiteContext.js";

function escapeAttr(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(s);
  }
  return s.replace(/["\\]/g, "\\$&");
}

function gridElement(gridId: string): HTMLElement | null {
  const el = document.querySelector(
    `[data-grid-container="${escapeAttr(gridId)}"],[data-grid="${escapeAttr(gridId)}"]`,
  );
  return el instanceof HTMLElement ? el : null;
}

/** Same measurement the in-canvas adapter uses for the nearest-child bump. */
function measureGridRect(gridId: string) {
  const el = gridElement(gridId);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
}

/** A grid's own box as the hit-tester reports it, for a grid the pointer is
 *  over but is not the DEEPEST one under it (an Inline's section). */
function resolveGrid(gridId: string): ResolvedGrid | null {
  const el = gridElement(gridId);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    gridId,
    originX: r.left,
    originY: r.top,
    width: r.width,
    scale: el.offsetWidth > 0 ? r.width / el.offsetWidth : 1,
  };
}

/** The nested grid of the Inline whose stored cells hold the pointer's cell
 *  in `grid`, if any. Layout px, the same cell math as `landingFromRect`. */
function inlineUnderCell(
  grids: ReturnType<typeof useGridStore.getState>["grids"],
  grid: ResolvedGrid,
  clientX: number,
  clientY: number,
): string | undefined {
  const g = grids[grid.gridId];
  if (!g) return undefined;
  const scale = grid.scale && grid.scale > 0 ? grid.scale : 1;
  const gap = gapOf(g.config);
  const cols = g.config.columns;
  const cellW = (grid.width / scale - gap.x * (cols - 1)) / cols;
  const col = Math.floor((clientX - grid.originX) / scale / (cellW + gap.x));
  const row = Math.floor(
    (clientY - grid.originY) / scale / (g.config.rowHeight + gap.y),
  );
  for (const it of Object.values(g.items)) {
    if (it.contentType !== "inline-grid" || !it.nestedGridId) continue;
    const p = it.position;
    if (col >= p.x && col < p.x + p.w && row >= p.y && row < p.y + p.h)
      return it.nestedGridId;
  }
  return undefined;
}

/** Resolve the accepted target grid + landing cell under a client point, or
 *  null when nothing under the pointer takes this kind. Exported for tests. */
export function crossTabTargetAt(
  clientX: number,
  clientY: number,
  meta: CrossTabMeta,
): UpdateDragTarget | null {
  const state = useGridStore.getState();
  const parentOf = (gid: string) => state.grids[gid]?.parentGridId;
  // The ONE membership gate (grid 0.9.4): the meta's contentType is the
  // dragged item as far as the rules read it (pinned "__" defaults never
  // ride a cross-tab drag — isCrossTabDraggable refuses them at the source).
  const inlineRowOf = inlineRowIn(state.grids);
  const accepts = (gid: string, _kind: string) =>
    crossGridAccepts(
      { contentType: meta.ct, w: meta.w },
      gid,
      parentOf,
      inlineRowOf,
    );

  const hit = gridUnderPoint(clientX, clientY);
  if (!hit) return null;
  let resolved: ResolvedGrid = hit;
  if (!accepts(hit.gridId, meta.kind) && inlineRowOf(hit.gridId)) {
    // An Inline's row that refuses (full, or the held block is a container)
    // hands the drop to the nearest ENCLOSING grid that takes it — the section
    // around it — instead of leaving the pointer over a dead zone.
    let up = parentOf(hit.gridId);
    while (up && !accepts(up, meta.kind)) up = parentOf(up);
    const around = up ? resolveGrid(up) : null;
    if (!around) return null;
    resolved = around;
  } else if (!accepts(hit.gridId, meta.kind)) {
    // The hovered grid refuses this kind (widget over a page body) — bump to
    // its nearest accepting child, exactly like the in-canvas adapter.
    const bumped = nearestAcceptedGridChild(
      state.grids,
      hit.gridId,
      meta.kind,
      accepts,
      clientX,
      clientY,
      measureGridRect,
    );
    if (!bumped) return null;
    // Re-resolve THROUGH the hit-tester at a point clamped inside the child,
    // so origin/width use the same content-box math as a direct hover.
    const rect = measureGridRect(bumped);
    if (!rect) return null;
    const cx = Math.min(Math.max(clientX, rect.left + 1), rect.right - 1);
    const cy = Math.min(Math.max(clientY, rect.top + 1), rect.bottom - 1);
    const rehit = gridUnderPoint(cx, cy);
    if (!rehit || rehit.gridId !== bumped) return null;
    resolved = rehit;
  }
  // An Inline wins its own cells. On the way to an Inline the pointer crosses
  // the section around it, and the section's preview reflow opens a slot
  // where the Inline sits, pushing it out from under the pointer before it
  // arrives. So when the pointer's cell in the hovered grid is one an Inline
  // occupies (its STORED cells — the preview may have moved its box) and the
  // Inline's row takes this block, target the row directly: the section
  // never reflows around it, and the row shows the one placeholder.
  const inline = inlineUnderCell(state.grids, resolved, clientX, clientY);
  if (inline && accepts(inline, meta.kind)) {
    const row = resolveGrid(inline);
    if (row) resolved = row;
  }
  const cfg = state.grids[resolved.gridId]?.config;
  if (!cfg) return null;
  const rowH = cfg.rowHeight;
  // Canvas zoom: gridUnderPoint measures SCREEN px, but cfg.rowHeight (and
  // the store's layout) are LAYOUT px. Since grid 0.9.4 the hit-tester is
  // scale-aware itself — `ResolvedGrid.scale` IS rect.width/offsetWidth —
  // so the local re-derivation (querying the container again) is gone.
  const scale = resolved.scale && resolved.scale > 0 ? resolved.scale : 1;
  const layoutW = resolved.width / scale;
  // Px-true size refit (the grid's own pxSizeToUnits, ex local refitUnits):
  // prefer the item's layout pixel size over its source units, so the slot
  // previews how big the item will actually be HERE.
  const dragged =
    meta.pw && meta.ph && layoutW > 0
      ? pxSizeToUnits(meta.pw, meta.ph, {
          cols: cfg.columns,
          rowHeight: rowH,
          gap: gapOf(cfg),
          rectWidth: layoutW,
        })
      : { w: meta.w, h: meta.h };
  const landing = landingFromRect(
    (clientX - resolved.originX) / scale,
    (clientY - resolved.originY) / scale,
    layoutW,
    cfg.columns,
    rowH,
    gapOf(cfg),
    dragged,
  );
  return { gridId: resolved.gridId, landing };
}

/** Copy the payload's asset bytes into the destination site's OPFS bucket.
 *  Content-addressed, so contentProps need no rewrite; failures are benign
 *  (the widget renders through the backend asset route like any
 *  collaborator-added image). */
export async function copyPayloadAssets(
  payload: CrossTabPayload,
  destSiteId: string,
): Promise<void> {
  if (payload.sourceSiteId === destSiteId) return;
  for (const a of payload.assets) {
    try {
      let blob = await getAssetFile(payload.sourceSiteId, a.sha256, a.ext);
      if (!blob) {
        const res = await fetch(
          `/api/v1/assets/${payload.sourceSiteId}/${a.sha256}`,
        );
        if (res.ok) blob = await res.blob();
      }
      if (blob) {
        // OPFS blobs come back typeless; retype with a lazy slice (no copy).
        const typed = blob.type ? blob : blob.slice(0, blob.size, a.mime);
        await putAsset(destSiteId, typed);
      }
    } catch {
      // Non-fatal by design (see above).
    }
  }
}

/**
 * Commit a validated payload at a client point: (re)start the external add
 * with the FULL spec so the item is born with its real contentProps — one
 * store commit, one undo gesture — then fire the drop reveal and the async
 * asset copy. Shared by the native-DnD drop (this file) and the broadcast
 * cross-window carry (crossWindowDrag). Returns the new item id, or null
 * when nothing under the point accepts the item.
 */
export function commitCrossTabPayload(
  payload: CrossTabPayload,
  meta: CrossTabMeta,
  clientX: number,
  clientY: number,
  destSiteId: string,
  targetAt: typeof crossTabTargetAt = crossTabTargetAt,
): string | null {
  const store = useGridStore.getState();
  const target = targetAt(clientX, clientY, meta);
  if (!target) return null;
  const id = store.startExternalAdd({
    contentType: payload.item.contentType,
    contentProps: payload.item.contentProps,
    w: meta.w,
    h: meta.h,
  });
  if (id === null) return null;
  store.updateExternalAdd(clientX, clientY, target);
  const newId = store.endExternalAdd();
  if (!newId) return null;
  markDropped(newId, { x: clientX, y: clientY });
  void copyPayloadAssets(payload, destSiteId);
  return newId;
}

export interface CrossTabDropController {
  onDragOver: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onDragEnd: () => void;
}

/** The handler set behind the hook — a plain factory so jsdom tests can
 *  drive it with hand-built events (jsdom has no DragEvent/DataTransfer). */
export function createCrossTabDrop(opts: {
  siteId: () => string | null;
  origin: string;
  /** Targeting seam — jsdom has no layout for `elementFromPoint`, so tests
   *  inject a resolver; production uses {@link crossTabTargetAt}. */
  targetAt?: typeof crossTabTargetAt;
}): CrossTabDropController {
  const targetAt = opts.targetAt ?? crossTabTargetAt;
  // One live session per tab: set once startExternalAdd succeeds.
  let activeMeta: CrossTabMeta | null = null;

  const reset = () => {
    activeMeta = null;
  };
  const cancel = () => {
    if (!activeMeta) return;
    useGridStore.getState().cancelExternalAdd();
    reset();
  };

  return {
    onDragOver(e) {
      const meta = activeMeta ?? parseMetaType(e.dataTransfer?.types);
      // Containers are P2 — refuse the drop (no preventDefault → no-drop cursor).
      if (!meta || meta.kind !== "app") return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      const store = useGridStore.getState();
      if (!activeMeta) {
        // Preview spec from the meta type only (contentProps arrive at drop).
        const id = store.startExternalAdd({
          contentType: meta.ct,
          w: meta.w,
          h: meta.h,
        });
        if (id === null) return; // another interaction is live — stand down
        activeMeta = meta;
      }
      store.updateExternalAdd(
        e.clientX,
        e.clientY,
        targetAt(e.clientX, e.clientY, meta),
      );
    },

    onDrop(e) {
      const meta = activeMeta ?? parseMetaType(e.dataTransfer?.types);
      if (!meta) return;
      e.preventDefault();
      const store = useGridStore.getState();
      const json = e.dataTransfer?.getData(WB_ITEM_TYPE) ?? "";
      const payload = parseCrossTabPayload(json, opts.origin);
      const destSiteId = opts.siteId();
      if (
        !payload ||
        payload.kind !== "app" ||
        !payloadMatchesMeta(payload, meta) ||
        !destSiteId
      ) {
        cancel();
        return;
      }
      // RESTART with the full spec so the committed item carries its real
      // contentProps from birth (see commitCrossTabPayload).
      if (activeMeta) store.cancelExternalAdd();
      reset();
      commitCrossTabPayload(
        payload,
        meta,
        e.clientX,
        e.clientY,
        destSiteId,
        targetAt,
      );
    },

    onDragLeave(e) {
      // relatedTarget null = the drag left the WINDOW (or was cancelled) —
      // intra-document leaves always name the element being entered.
      if (e.relatedTarget === null) cancel();
    },

    onDragEnd() {
      cancel();
    },
  };
}

/** Mount the cross-tab drop handlers for this editor (inert in read-only). */
export function useCrossTabDrop(readOnly: boolean): void {
  const siteId = useSiteId();
  const siteIdRef = useRef(siteId);
  siteIdRef.current = siteId;
  useEffect(() => {
    if (readOnly || typeof window === "undefined") return;
    const ctrl = createCrossTabDrop({
      siteId: () => siteIdRef.current,
      origin: window.location.origin,
    });
    window.addEventListener("dragover", ctrl.onDragOver);
    window.addEventListener("drop", ctrl.onDrop);
    window.addEventListener("dragleave", ctrl.onDragLeave);
    window.addEventListener("dragend", ctrl.onDragEnd);
    return () => {
      ctrl.onDragEnd();
      window.removeEventListener("dragover", ctrl.onDragOver);
      window.removeEventListener("drop", ctrl.onDrop);
      window.removeEventListener("dragleave", ctrl.onDragLeave);
      window.removeEventListener("dragend", ctrl.onDragEnd);
    };
  }, [readOnly]);
}
